/**
 * GCS Resumable Upload Client
 *
 * Handles direct browser-to-GCS resumable uploads with progress tracking.
 * Implements the GCS resumable upload protocol.
 */

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadOptions {
  sessionUrl?: string;
  signedUrl?: string; // Alternative to sessionUrl for signed URL uploads
  file: File;
  onProgress?: (progress: UploadProgress) => void;
  chunkSize?: number; // Default: 8MB
  maxRetries?: number; // Default: 3
  uploadMethod?: 'resumable' | 'signed'; // Which upload method to use
}

export interface UploadResult {
  success: boolean;
  error?: string;
  uploadedBytes?: number;
}

/**
 * Default chunk size for resumable uploads (8MB)
 */
const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;

/**
 * Default maximum retry attempts
 */
const DEFAULT_MAX_RETRIES = 3;

/**
 * Upload a file chunk to GCS
 */
async function uploadChunk(
  sessionUrl: string,
  chunk: Blob,
  startByte: number,
  endByte: number,
  totalSize: number
): Promise<Response> {
  const response = await fetch(sessionUrl, {
    method: 'PUT',
    mode: 'cors',
    headers: {
      'Content-Length': chunk.size.toString(),
      'Content-Range': `bytes ${startByte}-${endByte - 1}/${totalSize}`,
    },
    body: chunk,
  });

  return response;
}

/**
 * Query upload status to get current uploaded bytes
 */
async function queryUploadStatus(sessionUrl: string): Promise<number> {
  try {
    const response = await fetch(sessionUrl, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Length': '0',
        'Content-Range': 'bytes */*',
      },
    });

    if (response.status === 308) {
      // Resume incomplete upload
      const rangeHeader = response.headers.get('Range');
      if (rangeHeader) {
        const match = rangeHeader.match(/bytes=0-(\d+)/);
        if (match) {
          return parseInt(match[1], 10) + 1;
        }
      }
    }

    return 0;
  } catch (error) {
    console.error('Error querying upload status:', error);
    return 0;
  }
}

/**
 * Upload file using signed URL (direct PUT, bypasses CORS)
 * Uses XMLHttpRequest for better progress tracking
 */
async function uploadFileWithSignedUrl(
  signedUrl: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  maxRetries: number = DEFAULT_MAX_RETRIES
): Promise<UploadResult> {
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      // Use XMLHttpRequest for better progress tracking with signed URLs
      const result = await new Promise<UploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable && onProgress) {
            onProgress({
              loaded: e.loaded,
              total: e.total,
              percentage: (e.loaded / e.total) * 100,
            });
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200 || xhr.status === 201) {
            resolve({
              success: true,
              uploadedBytes: file.size,
            });
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed: network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });

      // If we get here, upload was successful
      return result;
    } catch (error) {
      retryCount++;
      if (retryCount >= maxRetries) {
        return {
          success: false,
          error: `Upload failed after ${maxRetries} retries: ${error instanceof Error ? error.message : 'Unknown error'}`,
          uploadedBytes: 0,
        };
      }
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
    }
  }

  return {
    success: false,
    error: 'Upload failed: max retries exceeded',
    uploadedBytes: 0,
  };
}

/**
 * Perform resumable upload with automatic retry and progress tracking
 * @param options - Upload options including file, URLs, and callbacks
 * @returns Upload result with success status and optional error message
 */
export async function uploadFile(options: UploadOptions): Promise<UploadResult> {
  const {
    sessionUrl,
    signedUrl,
    file,
    onProgress,
    chunkSize = DEFAULT_CHUNK_SIZE,
    maxRetries = DEFAULT_MAX_RETRIES,
    uploadMethod = signedUrl ? 'signed' : 'resumable',
  } = options;

  // Use signed URL upload if available (bypasses CORS)
  if (uploadMethod === 'signed' && signedUrl) {
    return uploadFileWithSignedUrl(signedUrl, file, onProgress, maxRetries);
  }

  // Fall back to resumable upload
  if (!sessionUrl) {
    return {
      success: false,
      error: 'No upload URL provided (neither sessionUrl nor signedUrl)',
      uploadedBytes: 0,
    };
  }

  let uploadedBytes = 0;
  let retryCount = 0;

  // Check if upload can be resumed
  try {
    const resumeByte = await queryUploadStatus(sessionUrl);
    if (resumeByte > 0 && resumeByte < file.size) {
      uploadedBytes = resumeByte;
      if (onProgress) {
        onProgress({
          loaded: uploadedBytes,
          total: file.size,
          percentage: (uploadedBytes / file.size) * 100,
        });
      }
    }
  } catch (error) {
    console.warn('Could not resume upload, starting from beginning:', error);
  }

  while (uploadedBytes < file.size && retryCount < maxRetries) {
    try {
      const endByte = Math.min(uploadedBytes + chunkSize, file.size);
      const chunk = file.slice(uploadedBytes, endByte);

      const response = await uploadChunk(sessionUrl, chunk, uploadedBytes, endByte, file.size);

      if (response.status === 200 || response.status === 201) {
        // Upload complete
        uploadedBytes = file.size;
        if (onProgress) {
          onProgress({
            loaded: file.size,
            total: file.size,
            percentage: 100,
          });
        }
        return { success: true, uploadedBytes: file.size };
      } else if (response.status === 308) {
        // Upload in progress, continue
        const rangeHeader = response.headers.get('Range');
        if (rangeHeader) {
          const match = rangeHeader.match(/bytes=0-(\d+)/);
          if (match) {
            uploadedBytes = parseInt(match[1], 10) + 1;
          } else {
            uploadedBytes = endByte;
          }
        } else {
          uploadedBytes = endByte;
        }

        if (onProgress) {
          onProgress({
            loaded: uploadedBytes,
            total: file.size,
            percentage: (uploadedBytes / file.size) * 100,
          });
        }

        retryCount = 0; // Reset retry count on successful chunk
      } else {
        throw new Error(`Upload failed with status ${response.status}`);
      }
    } catch (error) {
      retryCount++;
      if (retryCount >= maxRetries) {
        return {
          success: false,
          error: `Upload failed after ${maxRetries} retries: ${error instanceof Error ? error.message : 'Unknown error'}`,
          uploadedBytes,
        };
      }
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
    }
  }

  if (uploadedBytes < file.size) {
    return {
      success: false,
      error: 'Upload incomplete',
      uploadedBytes,
    };
  }

  return { success: true, uploadedBytes };
}

/**
 * Resume an interrupted upload
 * @param sessionUrl - The resumable upload session URL
 * @param file - The file to upload
 * @param onProgress - Optional progress callback
 * @returns Upload result with success status and optional error message
 */
export async function resumeUpload(
  sessionUrl: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  return uploadFile({
    sessionUrl,
    file,
    onProgress,
  });
}
